import { cn } from "@/lib/utils";
import React from "react";

/**
 * Typographic primitives.
 *
 * Each component renders a semantically correct element (p, h1, h2) and
 * applies the design-system type scale. Callers can always override the
 * className for local tweaks; callers can override the rendered element
 * via the `as` prop when layout demands it (e.g., a `Header` visually
 * styled but placed inside another heading context).
 *
 * NOTE: `Text/Title/Header` render an inline <span>, block <p>, and
 * block <h1>/<h2> respectively. The earlier implementation used <div>
 * everywhere, which broke screen-reader outlines.
 */

type AsProp<E extends React.ElementType> = {
  as?: E;
};

type PolymorphicProps<E extends React.ElementType, P = {}> = P &
  AsProp<E> &
  Omit<React.ComponentPropsWithoutRef<E>, keyof P | "as">;

/**
 * Text — body copy. Renders <p> by default (block).
 *
 * The legacy implementation rendered inline <div> with `text-xs`. We keep
 * the small size to avoid regressing every list row, but render <p> so
 * assistive tech can navigate by paragraph.
 */
export const Text = <E extends React.ElementType = "p">({
  as,
  className,
  ...props
}: PolymorphicProps<E>) => {
  const Component = (as ?? "p") as React.ElementType;
  return (
    <Component
      className={cn("text-xs text-muted-foreground", className)}
      {...props}
    />
  );
};

/**
 * Title — small section title. Renders <h3>.
 * Use for card section headings, list group titles.
 */
export const Title = <E extends React.ElementType = "h3">({
  as,
  className,
  ...props
}: PolymorphicProps<E>) => {
  const Component = (as ?? "h3") as React.ElementType;
  return (
    <Component
      className={cn(
        "text-base font-medium tracking-tight text-foreground",
        className,
      )}
      {...props}
    />
  );
};

/**
 * Header — page / major section header. Renders <h2> by default.
 * Use for the top-of-page label. For route-level page titles that need
 * <h1>, pass `as="h1"`.
 */
export const Header = <E extends React.ElementType = "h2">({
  as,
  className,
  ...props
}: PolymorphicProps<E>) => {
  const Component = (as ?? "h2") as React.ElementType;
  return (
    <Component
      className={cn(
        "text-xl font-semibold tracking-tight text-foreground",
        className,
      )}
      {...props}
    />
  );
};
